import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useActivityContext, ACTIVITY_TYPES } from "../../context/ActivityContext";
import {
  buildFetchParamsAll,
  buildFetchParamsNotes,
  mapEmailItems,
  mapNoteItems,
  mapSmsItems,
  buildAllMeta,
} from "../useActivityPage";

const DEFAULT_COLLECTION_STATE = {
  items: [],
  meta: { total_items: 0, total_pages: 1, current_page: 1, per_page: 20, has_next: false, has_previous: false, next_cursor: null },
  loading: false,
  error: null,
};

/**
 * Snapshot hook for Overview: feeds Recent Activity and Notes cards.
 * Calls both activity/list (Recent Activity) and activity/note/list (Notes card),
 * matching the Activity tab behavior (All tab vs Notes tab).
 */
export const useActivitySnapshot = () => {
  const { contactId } = useParams();
  const {
    collections,
    fetchActivity,
    clearActivityData,
    createNoteActivity,
    deleteNoteActivity,
    removeNoteOptimistically,
  } = useActivityContext();

  const hasAttemptedLoadRef = useRef(false);
  const lastContactIdRef = useRef(null);

  const emailCollection = collections[ACTIVITY_TYPES.EMAIL] ?? DEFAULT_COLLECTION_STATE;
  const notesCollection = collections[ACTIVITY_TYPES.NOTES] ?? DEFAULT_COLLECTION_STATE;
  const smsCollection = collections[ACTIVITY_TYPES.SMS] ?? DEFAULT_COLLECTION_STATE;

  const loadBase = useCallback(async () => {
    if (!contactId) return;
    hasAttemptedLoadRef.current = true;
    try {
      await fetchActivity(ACTIVITY_TYPES.ALL, buildFetchParamsAll(contactId));
      await fetchActivity(ACTIVITY_TYPES.NOTES, buildFetchParamsNotes(contactId));
    } catch {
      hasAttemptedLoadRef.current = false;
    }
  }, [contactId, fetchActivity]);

  useEffect(() => {
    if (contactId && lastContactIdRef.current !== null && lastContactIdRef.current !== contactId) {
      clearActivityData();
      hasAttemptedLoadRef.current = false;
    }
    lastContactIdRef.current = contactId;
  }, [contactId, clearActivityData]);

  useEffect(() => {
    if (!contactId) return;

    const hasData =
      emailCollection.items?.length > 0 ||
      notesCollection.items?.length > 0 ||
      smsCollection.items?.length > 0;

    if (hasData) {
      hasAttemptedLoadRef.current = true;
      return;
    }
    if (hasAttemptedLoadRef.current) return;

    loadBase();
  }, [
    contactId,
    emailCollection.items?.length,
    notesCollection.items?.length,
    smsCollection.items?.length,
    loadBase,
  ]);

  const emailItems = useMemo(() => mapEmailItems(emailCollection.items), [emailCollection.items]);
  const noteItems = useMemo(() => mapNoteItems(notesCollection.items), [notesCollection.items]);
  const smsItems = useMemo(() => mapSmsItems(smsCollection.items), [smsCollection.items]);

  const allItems = useMemo(() => {
    const combined = [
      ...emailItems.map((item) => ({ type: "Email", data: item, createdAt: item.createdAt })),
      ...noteItems.map((item) => ({ type: "Notes", data: item, createdAt: item.createdAt })),
      ...smsItems.map((item) => ({ type: "SMS", data: item, createdAt: item.createdAt })),
    ];
    return combined.sort((a, b) => {
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [emailItems, noteItems, smsItems]);

  const allCollection = useMemo(
    () => ({
      items: allItems,
      meta: buildAllMeta(allItems),
      loading: emailCollection.loading || notesCollection.loading || smsCollection.loading,
      error: emailCollection.error || notesCollection.error || smsCollection.error,
    }),
    [allItems, emailCollection.loading, emailCollection.error, notesCollection.loading, notesCollection.error, smsCollection.loading, smsCollection.error]
  );

  const handleRefresh = useCallback(() => {
    hasAttemptedLoadRef.current = false;
    loadBase();
  }, [loadBase]);

  const handleCreateNote = useCallback(
    async (noteData) => {
      const response = await createNoteActivity(noteData);
      await fetchActivity(ACTIVITY_TYPES.ALL, buildFetchParamsAll(contactId));
      await fetchActivity(ACTIVITY_TYPES.NOTES, buildFetchParamsNotes(contactId));
      return response;
    },
    [contactId, createNoteActivity, fetchActivity]
  );

  const handleDeleteNote = useCallback(
    async (noteUuid) => {
      removeNoteOptimistically(noteUuid);
      try {
        const response = await deleteNoteActivity(noteUuid);
        await fetchActivity(ACTIVITY_TYPES.ALL, buildFetchParamsAll(contactId));
        await fetchActivity(ACTIVITY_TYPES.NOTES, buildFetchParamsNotes(contactId));
        return response;
      } catch (err) {
        await fetchActivity(ACTIVITY_TYPES.ALL, buildFetchParamsAll(contactId));
        await fetchActivity(ACTIVITY_TYPES.NOTES, buildFetchParamsNotes(contactId));
        throw err;
      }
    },
    [contactId, deleteNoteActivity, fetchActivity, removeNoteOptimistically]
  );

  const getTabProps = useCallback(
    (tab) => {
      if (tab === ACTIVITY_TYPES.NOTES) {
        return {
          items: noteItems,
          loading: notesCollection.loading,
          error: notesCollection.error,
          refresh: handleRefresh,
        };
      }
      return {
        items: [],
        loading: false,
        error: null,
        refresh: () => Promise.resolve(null),
      };
    },
    [noteItems, notesCollection.loading, notesCollection.error, handleRefresh]
  );

  const isLoading = allCollection.loading && allItems.length === 0;

  return {
    contactId,
    allItems,
    noteItems,
    currentCollection: allCollection,
    isLoading,
    error: allCollection.error,
    handleRefresh,
    handleCreateNote,
    handleDeleteNote,
    getTabProps,
  };
};
